import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  ListSamplingPoliciesResponse,
  PutSamplingPoliciesRequest,
  PutSamplingPoliciesResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { getSamplingPolicies, putSamplingPolicies } from './samplingPolicies.ts';

export const SAMPLING_POLICIES_KEY = ['sampling-policies'] as const;

export function useSamplingPolicies(): UseQueryResult<ListSamplingPoliciesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: SAMPLING_POLICIES_KEY,
    queryFn: ({ signal }) => getSamplingPolicies(fetchImpl, signal),
  });
}

export function usePutSamplingPolicies(): UseMutationResult<
  PutSamplingPoliciesResponse,
  Error,
  PutSamplingPoliciesRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => putSamplingPolicies(body, fetchImpl),
    onSuccess: (response) => {
      // A dry run wrote nothing; the cached list is still the truth.
      if (response.dry_run) return;
      // The PUT answers with the full post-write state (new `version`
      // included), so the list can show it without waiting for a refetch.
      qc.setQueryData<ListSamplingPoliciesResponse>(SAMPLING_POLICIES_KEY, {
        policies: response.policies,
        version: response.version,
        sources: response.sources,
        legacy_lifecycle: response.legacy_lifecycle,
        write_gate: response.write_gate,
        lane_options: response.lane_options,
      });
    },
  });
}
